"use client"

import Link from "next/link"
import type { ComponentPropsWithoutRef, ReactNode } from "react"

type Variant = "primary" | "secondary" | "ghost" | "destructive" | "link"
type Size = "sm" | "md" | "lg"

type CommonProps = {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  children: ReactNode
  className?: string
}

type ButtonAsButton = CommonProps & { href?: undefined } & Omit<
  ComponentPropsWithoutRef<"button">,
  keyof CommonProps
>

type ButtonAsLink = CommonProps & { href: string; external?: boolean } & Omit<
  ComponentPropsWithoutRef<"a">,
  keyof CommonProps | "href"
>

export type ButtonProps = ButtonAsButton | ButtonAsLink

const SIZE_CLS: Record<Size, string> = {
  sm: "px-3 py-1.5 text-[13px] font-semibold rounded-[8px] min-h-[32px]",
  md: "px-4 py-2.5 text-sm font-semibold rounded-[10px] min-h-[40px]",
  lg: "px-5 py-3 text-[15px] font-bold rounded-[12px] min-h-[48px]",
}

const VARIANT_CLS: Record<Variant, string> = {
  primary:
    "text-white border-0 hover:-translate-y-px active:scale-[0.98]",
  secondary:
    "bg-[var(--redy-warm-white)] text-[var(--redy-charcoal)] border border-[var(--redy-border)] hover:bg-[var(--redy-border-l)]",
  ghost:
    "bg-transparent text-[var(--redy-secondary)] hover:bg-[var(--redy-warm-white)] hover:text-[var(--redy-charcoal)]",
  destructive:
    "bg-[var(--redy-red)] text-white border-0 hover:bg-[var(--redy-red-deep)] active:scale-[0.98]",
  link:
    "bg-transparent text-[var(--redy-red)] hover:text-[var(--redy-red-deep)] hover:underline !p-0 !min-h-0",
}

const BASE_CLS =
  "inline-flex items-center justify-center gap-2 transition-all duration-150 ease-out " +
  "disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--redy-red)]"

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="w-4 h-4 animate-spin shrink-0"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
      <path d="M22 12a10 10 0 0 1-10 10" strokeLinecap="round" />
    </svg>
  )
}

export function Button(props: ButtonProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = props as any
  const variant: Variant = p.variant ?? "primary"
  const size: Size = p.size ?? "md"
  const loading: boolean = p.loading ?? false
  const fullWidth: boolean = p.fullWidth ?? false

  const cls = [
    BASE_CLS,
    SIZE_CLS[size],
    VARIANT_CLS[variant],
    fullWidth ? "w-full" : "",
    loading ? "cursor-wait" : "",
    p.className ?? "",
  ]
    .filter(Boolean)
    .join(" ")

  const style =
    variant === "primary"
      ? { background: "var(--redy-gradient)" as const }
      : undefined

  const inner = (
    <>
      {loading ? <Spinner /> : p.leftIcon}
      <span>{p.children}</span>
      {!loading && p.rightIcon}
    </>
  )

  if (typeof p.href === "string") {
    const {
      variant: _v,
      size: _s,
      loading: _l,
      fullWidth: _fw,
      leftIcon: _li,
      rightIcon: _ri,
      children: _c,
      className: _cls,
      href,
      external,
      ...rest
    } = p
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          style={style}
          {...rest}
        >
          {inner}
        </a>
      )
    }
    return (
      <Link href={href} className={cls} style={style} {...rest}>
        {inner}
      </Link>
    )
  }

  const {
    variant: _v,
    size: _s,
    loading: __l,
    fullWidth: _fw,
    leftIcon: _li,
    rightIcon: _ri,
    children: _c,
    className: _cls,
    disabled,
    ...rest
  } = p
  // `disabled` va DESPUÉS del spread y fuera de `rest`: si el caller pasa
  // `disabled={false}` junto con `loading`, el spread no puede pisar el OR y el
  // botón queda bloqueado mientras carga (antes seguía clickeable con spinner).
  return (
    <button className={cls} style={style} {...rest} disabled={loading || disabled}>
      {inner}
    </button>
  )
}
